import {
  payerKycStatus,
  TPayerDetail,
  useDeletePayer,
  usePayer,
  useStartPayerKyc,
  useUpdatePayer,
} from '@/models/payers';
import SimulateSumsubWebhook, {
  SIMULATABLE_KYC_STATUSES,
} from '@/components/SimulateSumsubWebhook';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  CopyButton,
  Grid,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import {
  PiCheck,
  PiCopy,
  PiDownloadSimple,
  PiLinkSimple,
} from 'react-icons/pi';
import { Link, useLocation, useParams } from 'wouter';
import dayjs from 'dayjs';
import { semanticJurisdiction } from '@/lib/helpers';
import { KycStateBadge } from '@/components/PayerKycBadge';
import { useDownloadDocument } from '@/models/invoices';
import {
  PERMISSIONS,
  useHasAnyPermission,
  useHasPermission,
} from '@/models/permissions';
import { useConfirmation } from '@/hooks/useConfirmation';
import { listUrl } from '@/lib/listMemory';

const SESSION_COLORS: Record<string, string> = {
  approved: 'green',
  completed: 'green',
  rejected: 'red',
  failed: 'red',
  retry: 'orange',
  expired: 'gray',
  pending: 'yellow',
  in_progress: 'blue',
};

const SESSION_LABELS: Record<string, string> = {
  approved: 'Approved',
  completed: 'Completed',
  rejected: 'Rejected',
  failed: 'Failed',
  retry: 'Retry',
  expired: 'Expired',
  pending: 'Pending',
  in_progress: 'In Progress',
};

function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{children || '—'}</Text>
    </Box>
  );
}

function ClientDetailsGrid({ data }: { data: TPayerDetail }) {
  const d = data.clientDetails ?? {};
  if (data.clientType === 'Individual') {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Fact label="Full Name">{d.fullName}</Fact>
        <Fact label="Document Number">{d.documentNumber}</Fact>
        <Fact label="Nationality">{d.nationality}</Fact>
        <Fact label="Residence Country">
          {d.residenceCountry ? semanticJurisdiction(d.residenceCountry) : ''}
        </Fact>
        <Fact label="Legal Address">{d.legalAddress}</Fact>
      </SimpleGrid>
    );
  }
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      <Fact label="Company Name">{d.companyName}</Fact>
      <Fact label="Registration Number">{d.companyRegNumber}</Fact>
      <Fact label="Tax ID">{d.companyTaxId}</Fact>
      <Fact label="Country of Incorporation">
        {d.companyCountry ? semanticJurisdiction(d.companyCountry) : ''}
      </Fact>
      <Fact label="Registered Address">{d.companyLegalAddress}</Fact>
      <Fact label="Business Description">{d.businessDescription}</Fact>
      <Fact label="Website">
        {d.companyWebsite ? (
          <Anchor href={d.companyWebsite} target="_blank" fz="sm">
            {d.companyWebsite}
          </Anchor>
        ) : (
          ''
        )}
      </Fact>
    </SimpleGrid>
  );
}

export default function PayerDetail() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const [, nav] = useLocation();
  const { data, isLoading } = usePayer(id);

  const updatePayer = useUpdatePayer();
  const deletePayer = useDeletePayer();
  const startKyc = useStartPayerKyc();
  const download = useDownloadDocument();
  const canWrite = useHasAnyPermission(['payers:write', 'payers:*']);
  const canSimulate = useHasPermission(PERMISSIONS.RBAC_WRITE);
  const { confirm, ConfirmationDialog } = useConfirmation();

  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [editForm, setEditForm] = useState<{
    displayName?: string;
  }>({});

  if (isLoading || !data) return <Loader />;

  const kycStatus = payerKycStatus(data.kycExpiresAt);
  const isDeleted = !!data.deletedAt;
  const showActions = canWrite && !isDeleted;

  const handleGenerateLink = () => {
    startKyc.mutate(
      { payerId: id },
      {
        onSuccess: (res) => {
          navigator.clipboard?.writeText(res.url).catch(() => {});
          notifications.show({
            color: 'teal',
            title: res.reused
              ? 'Existing KYC link reused'
              : 'KYC link generated',
            message: 'The link has been copied to your clipboard.',
          });
        },
        onError: (err) => {
          notifications.show({ color: 'red', message: String(err) });
        },
      },
    );
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete Payer',
      message: `Delete payer "${data.displayName}"? This cannot be undone.`,
      confirmButtonText: 'Delete',
    });
    if (!ok) return;
    deletePayer.mutate(id, {
      onSuccess: () => nav(`~${listUrl('/payers')}`),
      onError: (err) => {
        const msg = String(err);
        if (msg.includes('409') || msg.toLowerCase().includes('referenced')) {
          notifications.show({
            color: 'orange',
            message: 'Cannot delete: payer is referenced by invoices.',
          });
        } else {
          notifications.show({ color: 'red', message: msg });
        }
      },
    });
  };

  const handleEdit = () => {
    updatePayer.mutate(
      { id, ...editForm },
      {
        onSuccess: () => closeEdit(),
        onError: (err) =>
          notifications.show({ color: 'red', message: String(err) }),
      },
    );
  };

  return (
    <Paper p="md" h="100%">
      <Container size="xl" px={0}>
        <ConfirmationDialog />
        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              <Card withBorder>
                <Group justify="space-between" align="flex-start">
                  <Stack gap={4}>
                    <Title order={3}>{data.displayName}</Title>
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        {data.clientType}
                      </Text>
                      <Text size="sm" c="dimmed">
                        ·
                      </Text>
                      <Text size="sm" c="dimmed">
                        {semanticJurisdiction(data.countryCode)}
                      </Text>
                      <KycStateBadge
                        kycExpiresAt={data.kycExpiresAt}
                        size="sm"
                      />
                      {isDeleted && (
                        <Badge size="sm" color="red" variant="filled">
                          Deleted {dayjs(data.deletedAt).format('DD.MM.YYYY')}
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                  {showActions && (
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          setEditForm({});
                          openEdit();
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        loading={deletePayer.isPending}
                        onClick={handleDelete}
                      >
                        Delete
                      </Button>
                    </Group>
                  )}
                </Group>
                {isDeleted && (
                  <Text size="xs" c="dimmed" mt="xs">
                    This payer is hidden from pickers and cannot be attached or
                    verified. Historical offers and invoices keep referencing
                    it.
                  </Text>
                )}
              </Card>

              <Card withBorder>
                <Title order={5} mb="sm">
                  {data.clientType === 'Individual'
                    ? 'Personal Details'
                    : 'Company Details'}
                </Title>
                <ClientDetailsGrid data={data} />
              </Card>

              <Card withBorder>
                <Group justify="space-between" mb="sm">
                  <Title order={5}>KYC Sessions</Title>
                  {showActions && (
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<PiLinkSimple size={14} />}
                      loading={startKyc.isPending}
                      onClick={handleGenerateLink}
                    >
                      {kycStatus === 'valid'
                        ? 'Generate KYC link'
                        : 'Request KYC'}
                    </Button>
                  )}
                </Group>
                {data.sessions.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No KYC sessions.
                  </Text>
                ) : (
                  <Table withRowBorders={false} verticalSpacing="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Link</Table.Th>
                        <Table.Th>Documents</Table.Th>
                        <Table.Th>Created</Table.Th>
                        <Table.Th>Completed</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.sessions.map((s) => {
                        const linkExpired =
                          !!s.sumsubUrlExpiresAt &&
                          new Date(s.sumsubUrlExpiresAt) <= new Date();
                        const docs = s.documents ?? [];
                        return (
                          <Table.Tr key={s.id}>
                            <Table.Td>
                              <Tooltip
                                label={`Review: ${s.reviewAnswer ?? '—'}`}
                                disabled={!s.reviewAnswer}
                              >
                                <Badge
                                  variant="light"
                                  size="sm"
                                  tt="none"
                                  style={{ whiteSpace: 'nowrap' }}
                                  color={SESSION_COLORS[s.status] ?? 'gray'}
                                >
                                  {SESSION_LABELS[s.status] ?? s.status}
                                </Badge>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                              {s.sumsubUrl ? (
                                <Group gap={4} wrap="nowrap">
                                  <CopyButton value={s.sumsubUrl}>
                                    {({ copied, copy }) => (
                                      <Tooltip
                                        label={
                                          copied ? 'Copied' : 'Copy KYC link'
                                        }
                                      >
                                        <ActionIcon
                                          size="sm"
                                          variant="subtle"
                                          color={copied ? 'teal' : 'gray'}
                                          onClick={copy}
                                        >
                                          {copied ? (
                                            <PiCheck size={14} />
                                          ) : (
                                            <PiCopy size={14} />
                                          )}
                                        </ActionIcon>
                                      </Tooltip>
                                    )}
                                  </CopyButton>
                                  {s.sumsubUrlExpiresAt && (
                                    <Text
                                      fz="xs"
                                      c={linkExpired ? 'red' : 'dimmed'}
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      {linkExpired ? 'expired' : 'till'}{' '}
                                      {dayjs(s.sumsubUrlExpiresAt).format(
                                        'DD.MM.YYYY',
                                      )}
                                    </Text>
                                  )}
                                  {canSimulate &&
                                    SIMULATABLE_KYC_STATUSES.includes(
                                      s.status,
                                    ) && (
                                      <SimulateSumsubWebhook sessionId={s.id} />
                                    )}
                                </Group>
                              ) : (
                                '—'
                              )}
                            </Table.Td>
                            <Table.Td>
                              {docs.length === 0 ? (
                                '—'
                              ) : (
                                <Menu shadow="md" width={280}>
                                  <Menu.Target>
                                    <Button
                                      size="compact-xs"
                                      variant="light"
                                      color="gray"
                                      leftSection={
                                        <PiDownloadSimple size={12} />
                                      }
                                    >
                                      {docs.length}{' '}
                                      {docs.length === 1 ? 'file' : 'files'}
                                    </Button>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    {docs.map((d) => (
                                      <Menu.Item
                                        key={d.id}
                                        leftSection={
                                          <PiDownloadSimple size={12} />
                                        }
                                        onClick={() => download.mutate(d.id)}
                                      >
                                        <Text fz="xs" truncate maw={220}>
                                          {d.name || d.filename}
                                        </Text>
                                      </Menu.Item>
                                    ))}
                                  </Menu.Dropdown>
                                </Menu>
                              )}
                            </Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                              {dayjs(s.createdAt).format('DD.MM.YYYY HH:mm')}
                            </Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                              {s.completedAt
                                ? dayjs(s.completedAt).format(
                                    'DD.MM.YYYY HH:mm',
                                  )
                                : '—'}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="md">
              <Card withBorder>
                <Title order={5} mb="sm">
                  Identity
                </Title>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <Fact label="Account">
                    {data.accountId ? (
                      <Anchor
                        component={Link}
                        to={`~/accounts/${data.accountId}`}
                        fz="sm"
                      >
                        {data.accountName ?? `#${data.accountId}`}
                      </Anchor>
                    ) : (
                      ''
                    )}
                  </Fact>
                  <Fact label="Invoice Email (verified)">
                    {data.verifiedEmail ? (
                      <Anchor href={`mailto:${data.verifiedEmail}`} fz="sm">
                        {data.verifiedEmail}
                      </Anchor>
                    ) : (
                      <Text fz="sm" c="orange.7">
                        None — invoices cannot be sent
                      </Text>
                    )}
                  </Fact>
                  <Fact label="KYC Approved">
                    {data.kycApprovedAt
                      ? dayjs(data.kycApprovedAt).format('DD.MM.YYYY')
                      : 'Not passed'}
                  </Fact>
                  <Fact label="KYC Expires">
                    {data.kycExpiresAt
                      ? dayjs(data.kycExpiresAt).format('DD.MM.YYYY')
                      : '—'}
                  </Fact>
                  <Fact label="Created">
                    {dayjs(data.createdAt).format('DD.MM.YYYY HH:mm')}
                  </Fact>
                  <Fact label="Updated">
                    {dayjs(data.updatedAt).format('DD.MM.YYYY HH:mm')}
                  </Fact>
                </SimpleGrid>
              </Card>

              {(data.mondayKycItemId ||
                data.mondayPayerId ||
                data.sumsubApplicantId) && (
                <Card withBorder>
                  <Title order={5} mb="sm">
                    External References
                  </Title>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    {data.mondayKycItemId && (
                      <Fact label="Monday KYC Item">
                        {String(data.mondayKycItemId)}
                      </Fact>
                    )}
                    {data.mondayPayerId && (
                      <Fact label="Monday Payer">
                        {String(data.mondayPayerId)}
                      </Fact>
                    )}
                    {data.sumsubApplicantId && (
                      <Fact label="SumSub Applicant">
                        {data.sumsubApplicantId}
                      </Fact>
                    )}
                  </SimpleGrid>
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        <Modal opened={editOpen} onClose={closeEdit} title="Edit Payer">
          <Stack gap="sm">
            <TextInput
              label="Display Name"
              defaultValue={data.displayName}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, displayName: e.target.value }))
              }
            />
            <TextInput label="Country Code" value={data.countryCode} readOnly />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeEdit}>
                Cancel
              </Button>
              <Button loading={updatePayer.isPending} onClick={handleEdit}>
                Save
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </Paper>
  );
}
